import { createSlice } from '@reduxjs/toolkit'

/**
 * Session slice tracks the JWT-related state plus the post-login
 * "select an active legal entity" gate.
 *
 * `requiresEntitySelection` and `availableLegalEntityIds` are populated
 * from the `/auth/signin` response when the authenticated user belongs
 * to multiple Legal_Entities (Requirement 2.10). While
 * `requiresEntitySelection` is true the user is treated as
 * authenticated for redux purposes (so the JWT can drive the
 * `/auth/switch-entity` call) but the `signedIn` flag stays false so
 * `useAuth().authenticated` keeps the router on the public side until
 * the user picks an entity. After a successful switch, the slice
 * receives the new token via `onSignInSuccess` and clears the gate.
 */
export const sessionSlice = createSlice({
    name: 'auth/session',
    initialState: {
        token: '',
        signedIn: false,
        requiresEntitySelection: false,
        availableLegalEntityIds: [],
    },
    reducers: {
        onSignInSuccess: (state, action) => {
            state.signedIn = true
            state.token = action.payload
            state.requiresEntitySelection = false
            state.availableLegalEntityIds = []
        },
        onSignOutSuccess: (state) => {
            state.signedIn = false
            state.token = ''
            state.requiresEntitySelection = false
            state.availableLegalEntityIds = []
        },
        setToken: (state, action) => {
            state.token = action.payload
        },
        // Login succeeded but the user must pick an active Legal_Entity
        // before accessing the application (Requirement 2.10). We park
        // the JWT and the available entities so the entity-selection
        // screen can call `/auth/switch-entity`.
        onEntitySelectionRequired: (state, action) => {
            const { token, availableLegalEntityIds = [] } = action.payload || {}
            state.token = token || ''
            state.signedIn = false
            state.requiresEntitySelection = true
            state.availableLegalEntityIds = availableLegalEntityIds
        },
    },
})

export const {
    onSignInSuccess,
    onSignOutSuccess,
    setToken,
    onEntitySelectionRequired,
} = sessionSlice.actions

export default sessionSlice.reducer
